import React, { useState } from "react"
import {
    Box,
    TextField,
    MenuItem,
    Button,
    InputAdornment,
    Typography,
    Chip,
} from "@mui/material"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import QueryBuilderIcon from "@mui/icons-material/QueryBuilder"
import PersonIcon from "@mui/icons-material/Person"
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import SectionBox from "./SectionBox"

export default function OnSiteChecksTab() {
    const [project, setProject] = useState("Office Building Renovation")
    const [date, setDate] = useState("Nov 12")
    const [time, setTime] = useState("09:47 AM")
    const [inspector, setInspector] = useState("Alex Thompson")

    const handleStartCheck = () => {
        console.log("Starting site check with:", { project, date, time, inspector })
    }

    const inputStyle = {
        backgroundColor: "#F9FAFB",
        borderRadius: "8px",
        minWidth: "220px",
        flex: 1,
        "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: "#E4E7EC" },
            "&:hover fieldset": { borderColor: "#D0D5DD" },
            "&.Mui-focused fieldset": { borderColor: "#6A00FF" },
            height: "40px",
        },
        "& .MuiInputBase-input": {
            fontSize: "14px",
            color: "#101828",
        },
    }

    const projects = [
        "Office Building Renovation",
        "Downtown Office Complex",
        "Shopping Mall Upgrade",
    ]

    const inspectors = ["Alex Thompson", "Maria Lopez", "David Chen"]

    const visits = [
        {
            id: 1,
            date: "Nov 12, 2025 at 09:30",
            project: "Office Building Renovation",
            inspector: "Alex Thompson",
            contractor: "ABC Construction",
            attendance: "8/10",
            issues: 2,
            status: "Completed",
        },
    ]

    return (
        <Box sx={{ mt: "3vh" }}>
            {/* Start Site Visit */}
            <SectionBox
                icon={<AccessTimeIcon sx={{ fontSize: 20, color: "#101828" }} />}
                title="Start Site Visit"
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        gap: "1rem",
                        flexWrap: "wrap",
                    }}
                >
                    {/* Project */}
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                        <label
                            style={{
                                fontSize: "13px",
                                color: "#344054",
                                fontWeight: 500,
                                marginBottom: "4px",
                            }}
                        >
                            Project
                        </label>
                        <TextField
                            select
                            size="small"
                            value={project}
                            onChange={(e) => setProject(e.target.value)}
                            sx={inputStyle}
                        >
                            {projects.map((p, i) => (
                                <MenuItem key={i} value={p}>
                                    {p}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    {/* Date */}
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                        <label
                            style={{
                                fontSize: "13px",
                                color: "#344054",
                                fontWeight: 500,
                                marginBottom: "4px",
                            }}
                        >
                            Date
                        </label>
                        <TextField
                            value={date}
                            size="small"
                            sx={inputStyle}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarMonthIcon sx={{ color: "#667085", fontSize: 18 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {/* Time */}
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                        <label
                            style={{
                                fontSize: "13px",
                                color: "#344054",
                                fontWeight: 500,
                                marginBottom: "4px",
                            }}
                        >
                            Time
                        </label>
                        <TextField
                            value={time}
                            size="small"
                            sx={inputStyle}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <QueryBuilderIcon sx={{ color: "#667085", fontSize: 18 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {/* Inspector */}
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                        <label
                            style={{
                                fontSize: "13px",
                                color: "#344054",
                                fontWeight: 500,
                                marginBottom: "4px",
                            }}
                        >
                            Inspector
                        </label>
                        <TextField
                            select
                            size="small"
                            value={inspector}
                            onChange={(e) => setInspector(e.target.value)}
                            sx={inputStyle}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonIcon sx={{ color: "#667085", fontSize: 18 }} />
                                    </InputAdornment>
                                ),
                            }}
                        >
                            {inspectors.map((p, i) => (
                                <MenuItem key={i} value={p}>
                                    {p}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    {/* Start Check Button */}
                    <Button
                        variant="contained"
                        onClick={handleStartCheck}
                        sx={{
                            backgroundColor: "#6A00FF",
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: 500,
                            borderRadius: "8px",
                            height: "40px",
                            px: 3,
                            "&:hover": { backgroundColor: "#5a00d6" },
                        }}
                    >
                        Start Check
                    </Button>
                </Box>
            </SectionBox>

            {/* Recent Visits */}
            <SectionBox
                icon={<FilterAltOutlinedIcon sx={{ fontSize: 20, color: "#101828" }} />}
                title="Recent Visits"
            >
                {/* Filters Row */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(6, 1fr)",
                        gap: "1rem",
                        alignItems: "end",
                        mb: "1.5rem",
                        width: "100%",
                        overflowX: "auto",
                        overflowY: "hidden", // ✅ disable vertical scroll
                        pb: 1, // small padding bottom to avoid scroll bar clipping
                    }}
                >
                    {[
                        "Date From",
                        "Date To",
                        "Project Name",
                        "Project ID",
                        "Inspector",
                        "Contractor",
                    ].map((label, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                minWidth: "160px", // ✅ slightly larger width for comfort
                            }}
                        >
                            <label
                                style={{
                                    fontSize: "13px",
                                    color: "#344054",
                                    fontWeight: 500,
                                    marginBottom: "4px",
                                }}
                            >
                                {label}
                            </label>

                            {/* Conditional rendering for dropdowns */}
                            {label === "Inspector" || label === "Contractor" ? (
                                <TextField
                                    select
                                    size="small"
                                    value="all" // ✅ Correct default
                                    sx={{
                                        ...inputStyle,
                                        "& .MuiOutlinedInput-root": {
                                            ...inputStyle["& .MuiOutlinedInput-root"],
                                            height: "44px", // ✅ slightly taller
                                        },
                                    }}
                                >
                                    {label === "Inspector" ? (
                                        <>
                                            <MenuItem value="all">All Inspectors</MenuItem>
                                            <MenuItem value="Alex Thompson">Alex Thompson</MenuItem>
                                            <MenuItem value="Maria Lopez">Maria Lopez</MenuItem>
                                            <MenuItem value="David Chen">David Chen</MenuItem>
                                        </>
                                    ) : (
                                        <>
                                            <MenuItem value="all">All Contractors</MenuItem>
                                            <MenuItem value="ABC Construction">ABC Construction</MenuItem>
                                            <MenuItem value="XYZ Electric">XYZ Electric</MenuItem>
                                            <MenuItem value="Modern Plumbing">Modern Plumbing</MenuItem>
                                        </>
                                    )}
                                </TextField>
                            ) : (
                                <TextField
                                    placeholder={
                                        label === "Date From" || label === "Date To"
                                            ? "Select date"
                                            : label === "Project Name"
                                                ? "Search project..."
                                                : "Project ID..."
                                    }
                                    size="small"
                                    sx={{
                                        ...inputStyle,
                                        "& .MuiOutlinedInput-root": {
                                            ...inputStyle["& .MuiOutlinedInput-root"],
                                            height: "44px", // ✅ consistent field height
                                        },
                                    }}
                                    InputProps={{
                                        startAdornment:
                                            label.includes("Date") ? (
                                                <InputAdornment position="start">
                                                    <CalendarMonthIcon sx={{ color: "#667085", fontSize: 18 }} />
                                                </InputAdornment>
                                            ) : undefined,
                                    }}
                                />
                            )}
                        </Box>
                    ))}
                </Box>



                {/* Table Header */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns:
                            "2fr 2fr 2fr 2fr 1fr 1fr 1fr",
                        color: "#667085",
                        fontSize: "10px",
                        mb: 1,
                    }}
                >
                    <Typography sx={{ fontSize: "13px" }}>Date/Time</Typography>
                    <Typography sx={{ fontSize: "13px" }}>Project</Typography>
                    <Typography sx={{ fontSize: "13px" }}>Inspector</Typography>
                    <Typography sx={{ fontSize: "13px" }}>Contractor</Typography>
                    <Typography sx={{ fontSize: "13px" }}>Attendance</Typography>
                    <Typography sx={{ fontSize: "13px" }}>Issues</Typography>
                    <Typography sx={{ fontSize: "13px" }}>Status</Typography>
                </Box>

                <Box sx={{ borderBottom: "1px solid #E4E7EC", mb: 1 }} />

                {/* Table Rows */}
                {visits.map((v) => (
                    <Box
                        key={v.id}
                        sx={{
                            display: "grid",
                            gridTemplateColumns:
                                "2fr 2fr 2fr 2fr 1fr 1fr 1fr",
                            alignItems: "center",
                            fontSize: "10px",
                            py: 1,
                            color: "#101828",
                        }}
                    >
                        <Typography sx={{ fontSize: "13px" }}>{v.date}</Typography>
                        <Typography sx={{ fontSize: "13px" }}>{v.project}</Typography>
                        <Typography sx={{ fontSize: "13px" }}>{v.inspector}</Typography>
                        <Typography sx={{ fontSize: "13px" }}>{v.contractor}</Typography>
                        <Chip
                            label={v.attendance}
                            size="small"
                            sx={{
                                backgroundColor: "#F9FAFB",
                                border: "1px solid #E4E7EC",
                                fontWeight: 600,
                                fontSize: "13px",
                                width: "50px",
                            }}
                        />
                        <Chip
                            icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
                            label={v.issues}
                            size="small"
                            sx={{
                                backgroundColor: "#FEE4E2",
                                color: "#D92D20",
                                width: "50px",
                                fontWeight: 600,
                                fontSize: "13px",
                                "& .MuiChip-icon": { color: "#D92D20", ml: "2px" },
                            }}
                        />
                        <Chip
                            label={v.status}
                            size="small"
                            sx={{
                                backgroundColor: "#6A00FF",
                                color: "#fff",
                                fontWeight: 500,
                                fontSize: "13px",
                                width: "auto",
                            }}
                        />
                    </Box>
                ))}
            </SectionBox>
        </Box>
    )
}
